import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, Phone, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { PHONE_DISPLAY, PHONE_LINK } from "@/data/content";

const LINKS = [
  { to: "/", label: "Accueil", id: "accueil" },
  { to: "/services", label: "Nos services", id: "services" },
  { to: "/a-propos", label: "À propos", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-gold/15 bg-noir">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5">
            <LogoMark className="h-11 w-11 shrink-0" />
            <span className="flex flex-col leading-none">
              <span className="block font-serif text-2xl tracking-[0.16em] text-gold md:text-[26px]">
                PUREVA
              </span>
              <span
                data-testid="nav-brand-tagline"
                className="mt-0.5 block text-[9.5px] font-medium uppercase tracking-[0.04em] text-ivory/85"
              >
                Nettoyage haut de gamme
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-5 md:flex lg:gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative pb-1.5 text-[11px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 lg:text-[12px] lg:tracking-[0.16em] ${
                    isActive ? "text-gold" : "text-ivory/85 hover:text-gold"
                  }`
                }
              >
                {l.label}
                <span
                  className={`absolute bottom-0 left-0 h-px bg-gold transition-all duration-300 ${
                    location.pathname === l.to ? "w-full" : "w-0 hover:w-full"
                  }`}
                />
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-quote"
              className="rounded-md border border-gold/70 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold transition-colors duration-300 hover:bg-gold hover:text-noir lg:px-6"
            >
              Demander une soumission
            </Link>
          </nav>

          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-gold/50 text-gold md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-noir md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-1 flex-col justify-center gap-1 px-8 pt-24">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-gold/10"
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-5 font-serif text-3xl ${
                        isActive ? "italic text-gold" : "text-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8 flex flex-col gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="nav-mobile-cta-quote"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-6 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-noir"
                >
                  Demander une soumission
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={PHONE_LINK}
                  data-testid="nav-mobile-phone"
                  className="inline-flex items-center justify-center gap-3 rounded-md border border-gold/40 px-6 py-4 text-sm tracking-[0.12em] text-gold"
                >
                  <Phone className="h-4 w-4" />
                  {PHONE_DISPLAY}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
